import React, { FC } from "react";
import {
  makeStyles,
  Box,
  IconButton,
  Typography,
  FormLabel,
  debounce,
} from "@material-ui/core";
import colors from "../constants/colors";
import { observer } from "mobx-react-lite";

interface IParameterProps {
  parameter: string;
  units: string;
  name: string;
  handleChange: (name: string, value: number) => void;
  increment: (name: string, scale: number) => void;
  decrement: (name: string, scale: number) => void;
  value: number;
  min: number;
  max: number;
  Tooltip?: JSX.Element;
  disabled?: boolean;
  required?: boolean;
}

export const Parameter: FC<IParameterProps> = observer(
  ({
    handleChange,
    parameter,
    units,
    name,
    increment,
    decrement,
    value,
    min,
    max,
    Tooltip,
    disabled,
    required = false,
  }) => {
    const classes = styles();

    const getScale = () =>
      units.toLowerCase() === "degrees"
        ? 10
        : units.toLowerCase() === "mm"
        ? 50
        : units.toLowerCase() === "in"
        ? 1
        : 1000;

    const onInput = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Number(e.target.value);
      if (!isNaN(newValue) && newValue >= min && newValue <= max) {
        return handleChange(e.target.name, newValue);
      }
      e.target.value = value.toString();
    }, 800);

    const onPress = (e, method: "increment" | "decrement") => {
      const scale = getScale();
      const newValue = method === "increment" ? value + scale : value - scale;

      if (newValue >= min && newValue <= max) {
        return method === "increment"
          ? increment(e.currentTarget.name, scale)
          : decrement(e.currentTarget.name, scale);
      }

      handleChange(e.currentTarget.name, method === "increment" ? min : max);
    };

    return (
      <Box className={classes.container}>
        <Typography className={classes.parameterText}>
          {parameter} {required && "*"} {Tooltip && Tooltip}
        </Typography>
        <Box className={classes.control}>
          <Box component="div" className={classes.controlContainer}>
            <IconButton
              disabled={value === min}
              name={name}
              onClick={(e) => !disabled && onPress(e, "decrement")}
            >
              <Box component="span" className={classes.inputDial}>
                -
              </Box>
            </IconButton>
            <FormLabel className={classes.units}>{min.toFixed(0)}</FormLabel>
          </Box>
          <Box className={classes.inputContainer}>
            <input
              disabled={disabled}
              key={value}
              min={min}
              max={max}
              onChange={(e) => {
                e.persist();
                onInput(e);
              }}
              name={name}
              className={classes.input}
              defaultValue={value === 0 ? "" : value}
            />
            <FormLabel className={classes.units}>{units}</FormLabel>
          </Box>
          <Box component="div" className={classes.controlContainer}>
            <IconButton
              disabled={value === max}
              name={name}
              onClick={(e) => !disabled && onPress(e, "increment")}
            >
              <Box component="span" className={classes.inputDial}>
                +
              </Box>
            </IconButton>
            <FormLabel className={classes.units}>{max.toFixed(0)}</FormLabel>
          </Box>
        </Box>
      </Box>
    );
  }
);

const styles = makeStyles(() => ({
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  control: {
    display: "flex",
    alignItems: "center",
  },
  controlContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  parameterText: {
    textTransform: "uppercase",
    fontWeight: "bold",
    fontSize: 12,
    letterSpacing: 1.5,
    color: colors.dark,
    maxWidth: 100,
  },
  parameter: {
    display: "flex",
  },
  input: {
    display: "block",
    width: 50,
    height: 50,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: "1px",
    borderStyle: "solid",
    outline: "none",
    textAlign: "center",
  },
  inputDial: {
    color: colors.primary,
    position: "relative",
    fontWeight: "bold",
  },
  inputContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "0px 10px",
  },
  units: {
    fontSize: 10,
  },
}));
